import { Helmet } from 'react-helmet';

interface OrganizationSchemaProps {
  name?: string;
  description?: string;
  url?: string;
}

export function OrganizationSchema({
  name = "Samboards",
  description = "UK-based manufacturer of premium handmade tactile sensory boards for neurodivergent individuals, ADHD, autism, anxiety, and sensory needs.",
  url = typeof window !== 'undefined' ? window.location.origin : ''
}: OrganizationSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": name,
    "description": description,
    "url": url,
    "logo": `${url}/samboards-logo.png`,
    "foundingLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "GB"
      }
    },
    "award": "Medilink Finalist 2025",
    "slogan": "Premium tactile sensory boards for neurodivergent minds",
    "areaServed": "United Kingdom",
    "knowsAbout": [
      "Sensory tools",
      "Tactile boards",
      "ADHD support",
      "Autism support",
      "Anxiety management",
      "Focus tools",
      "Neurodiversity"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface ProductSchemaProps {
  name: string;
  description: string;
  price: number;
  image?: string;
  sku?: string;
  availability?: string;
}

export function ProductSchema({
  name,
  description,
  price,
  image,
  sku,
  availability = "https://schema.org/InStock"
}: ProductSchemaProps) {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "brand": {
      "@type": "Brand",
      "name": "Samboards"
    },
    "offers": {
      "@type": "Offer",
      "price": price.toFixed(2),
      "priceCurrency": "GBP",
      "availability": availability,
      "url": typeof window !== 'undefined' ? window.location.href : ''
    },
    ...(image && { "image": `${siteUrl}${image}` }),
    ...(sku && { "sku": sku })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbSchema({ items }: { items: BreadcrumbItem[] }) {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteUrl}${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export function WebsiteSchema() {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Samboards",
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/shop?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface ArticleSchemaProps {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
  keywords?: string[];
}

export function ArticleSchema({
  headline,
  description,
  datePublished,
  dateModified,
  author = "Samboards Research Team",
  image,
  keywords = []
}: ArticleSchemaProps) {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "author": {
      "@type": "Organization",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Samboards",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/samboards-logo.png`
      }
    },
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    ...(image && { "image": `${siteUrl}${image}` }),
    ...(keywords.length > 0 && { "keywords": keywords.join(", ") })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export function LocalBusinessSchema() {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Samboards",
    "description": "UK-based small manufacturer of handmade sensory tools and tactile boards for neurodivergent individuals.",
    "url": siteUrl,
    "logo": `${siteUrl}/samboards-logo.png`,
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "slogan": "Handmade in the UK"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}